import {
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Consumer} from '@emm/core/models/consumer.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {MenuFiltersFacadeService} from '@emm/menu/services/menu-filters-facade.service';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {LocationSetType} from '@emm/core/models/location-set-type.model';
import {LocationSet} from '@emm/core/models/location-set.model';
import {Venue} from '@emm/core/models/venue.model';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';

const WIDTH_450 = '450px';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-menu-filters',
    styleUrls: ['./menu-filters.component.scss'],
    templateUrl: './menu-filters.component.html',
    standalone: false
})
export class MenuFiltersComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    @Input() filtersWidth: string;
    @Output() deleteConsumer = new EventEmitter<Consumer>();

    changeSets$ = this.filtersFacade.changeSets$;
    childLocationSets$ = this.filtersFacade.childLocationSets$;
    consumers$ = this.filtersFacade.consumers$;
    effectiveDate$ = this.filtersFacade.effectiveDate$;
    isChainMenu$ = this.filtersFacade.isChainMenu$;
    isLoading$ = this.filtersFacade.isLoading$;
    isLocationSetTypeParent$ = this.filtersFacade.isLocationSetTypeParent$;
    isReadOnly$ = this.authFacade.isReadOnly$;
    locationSetTypes$ = this.filtersFacade.locationSetTypes$;
    parentLabel$ = this.filtersFacade.parentLabel$;
    parentLocationSets$ = this.filtersFacade.parentLocationSets$;
    selectedFeatureScope$ = this.filtersFacade.selectedFeatureScope$;
    selectedLocationSetType$ = this.filtersFacade.selectedLocationSetType$;
    selectedLocationSet$ = this.filtersFacade.selectedLocationSet$;
    selectedConsumer$ = this.filtersFacade.selectedConsumer$;
    selectedSubmenu$ = this.filtersFacade.selectedSubmenu$;
    selectedVenue$ = this.filtersFacade.selectedVenue$;
    venues$ = this.filtersFacade.venues$;
    removeLocationsInGmb$ = this.launchDarklyService.getBooleanFlag(FlagKeys.REMOVE_LOCATIONS_IN_GMB);
    isChain = false;
    selectedFeatureScope;
    selectedLocationSetType;
    selectedLocationSet;
    selectedConsumer;
    previousConsumer;
    selectedVenue;
    selectedSubmenu;

    constructor(
        private authFacade: AuthFacadeService,
        private filtersFacade: MenuFiltersFacadeService,
        private launchDarklyService: LaunchDarklyService) {}

    ngOnInit() {
        this.selectedFeatureScope$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe((scope: FeatureScope) => this.selectedFeatureScope = scope);
        this.selectedLocationSetType$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe((type: LocationSetType) => type ? this.selectedLocationSetType = type : null);
        this.selectedLocationSet$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            ).subscribe((set: LocationSet) => set ? this.selectedLocationSet = set : null);
        this.selectedConsumer$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((consumer: Consumer) => {
                if (consumer) {
                    this.selectedConsumer = consumer;
                    this.previousConsumer = this.selectedConsumer;
                } else if (this.previousConsumer) {
                    this.filtersFacade.selectConsumer(this.previousConsumer?.tag);
                }
            });
        this.selectedVenue$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((venue: Venue) => venue ? this.selectedVenue = venue : null);
        this.selectedSubmenu$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((submenu: string) => submenu ? this.selectedSubmenu = submenu : null);
    }

    changeEffectiveDate(date: string): void {
        this.filtersFacade.changeEffectiveDate(date);
    }

    featureScopeSelected(scope: string): void {
        this.filtersFacade.selectFeatureScope(scope);
        if (this.selectedLocationSetType) {
            this.filtersFacade.selectLocationSetType(this.selectedLocationSetType?.tag);
        }
        if (this.selectedLocationSet) {
            this.filtersFacade.selectLocationSet(this.selectedLocationSet?.tag);
        }
        if (this.selectedConsumer && scope === 'Global') {
            this.filtersFacade.selectVenue(null);
        }
        if (this.selectedConsumer && scope !== 'Global') {
            this.filtersFacade.selectConsumer(this.selectedConsumer?.tag);
        }
        if (this.selectedVenue && scope === 'Viewer') {
            this.filtersFacade.selectVenue(this.selectedVenue);
        }
        if (scope === 'Client') {
            this.isChain = true;
            this.filtersFacade.selectLocationSetType('CHAIN');
            this.filtersFacade.selectLocationSet('CHAIN');
            if (this.selectedConsumer?.tag) {
                this.filtersFacade.selectConsumer(this.selectedConsumer?.tag);
            }
        }
    }

    locationSetTypeSelected(selected: LocationSetType): void {
        if (this.selectedVenue) {
            this.filtersFacade.selectVenue(null);
        }
        selected?.tag === 'CHAIN' ? this.isChain = true : this.isChain = false;
        this.filtersFacade.selectLocationSetType(selected?.tag);
        if (this.isChain) {
            this.filtersFacade.selectLocationSet('CHAIN');
        }
    }

    locationSetSelected(selected: LocationSet): void {
        this.filtersFacade.selectLocationSet(selected?.tag);
    }

    venueSelected(selected: Venue) {
        this.filtersFacade.selectVenue(selected);
    }

    resetSelectedLocationSet(): void {
        this.filtersFacade.selectLocationSet(null);
    }

    resetSelectedVenue(): void {
        this.filtersFacade.selectVenue(null);
    }

    consumerSelected(selected: Consumer): void {
        this.filtersFacade.selectConsumer(selected?.tag);
    }

    filterLocationSets(locationSetFilter: string): void {
        this.filtersFacade.applyLocationSetFilter(locationSetFilter);
    }

    parentLocationSetSelected(selected: LocationSet): void {
        this.filtersFacade.selectParentLocationSet(selected?.tag);
    }
}
